import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { MessageSquare } from "lucide-react";
import { Column, DataTable } from "@/components/ui/data-table";
import { Button } from "@/components/ui/button";
import { NameCell } from "@/components/pages/feedbacks/components/DataTable/cells/name";
import { RatingStars } from "@/components/pages/requests/components/activity/RatingStars";
import { MetricCard, MetricCardsGrid } from "../shared";
import { RevenueChartCard } from "../revenue/RevenueChartCard";
import { SERVICES_CHART_CARD_ICON_COLOR } from "./constants";
import { ANALYTICS_TOOLBAR_BUTTON_CLASS } from "../shared/toolbarButtonStyles";
import {
  SERVICE_REVIEWS_PAGE_SIZE,
  type ServiceReviewRow,
  type ServiceReviewSummary,
} from "./serviceDetailTypes";

interface ServiceRelatedReviewsSectionProps {
  summary: ServiceReviewSummary;
  reviews: ServiceReviewRow[];
  onExport?: (format: "csv" | "xlsx") => void;
  exportDisabled?: boolean;
}

export const ServiceRelatedReviewsSection: React.FC<
  ServiceRelatedReviewsSectionProps
> = ({ summary, reviews, onExport, exportDisabled }) => {
  const { t } = useTranslation();
  const [currentPage, setCurrentPage] = useState(1);

  const totalPages = Math.max(
    1,
    Math.ceil(reviews.length / SERVICE_REVIEWS_PAGE_SIZE),
  );
  const safePage = Math.min(currentPage, totalPages);

  const paginatedReviews = useMemo(() => {
    const start = (safePage - 1) * SERVICE_REVIEWS_PAGE_SIZE;
    return reviews.slice(start, start + SERVICE_REVIEWS_PAGE_SIZE);
  }, [reviews, safePage]);

  const columns: Column<ServiceReviewRow>[] = useMemo(
    () => [
      {
        key: "guestName",
        header: t("analytics.common.name", "Name"),
        className: "min-w-[160px]",
        render: (_, row) => (
          <NameCell name={row.guestName} countryCode={row.countryCode} />
        ),
      },
      {
        key: "responsibility",
        header: t(
          "analytics.services.detail.reviews.responsibility",
          "Responsibility",
        ),
        className: "text-center text-[#171717]",
        render: (value) => <span>{value}</span>,
      },
      {
        key: "feedbackTime",
        header: t(
          "analytics.services.detail.reviews.feedbackTime",
          "Feedback time",
        ),
        className: "text-center text-[#737373] text-xs whitespace-nowrap",
        render: (value) => <span>{value}</span>,
      },
      {
        key: "feedback",
        header: t("analytics.services.detail.reviews.feedback", "Feedback"),
        className: "min-w-[200px] text-[#171717] text-sm",
        render: (value) => <span className="line-clamp-2">{value}</span>,
      },
      {
        key: "rating",
        header: t("analytics.services.detail.reviews.rating", "Rating"),
        className: "text-center",
        render: (_, row) => (
          <RatingStars
            value={row.rating}
            readOnly
            size={14}
            className="justify-center"
          />
        ),
      },
    ],
    [t],
  );

  return (
    <RevenueChartCard
      title={t("analytics.services.detail.reviews.title", "Related Reviews")}
      icon={<MessageSquare />}
      iconColor={SERVICES_CHART_CARD_ICON_COLOR}
      className="w-full"
      onExport={onExport}
      exportDisabled={exportDisabled}
    >
      <div className="mb-4 flex flex-wrap items-center justify-start gap-2">
        <Button
          type="button"
          variant="span"
          disabled
          title={t(
            "analytics.common.comingSoon.evaluations",
            "Evaluations will be available with backend data",
          )}
          className={ANALYTICS_TOOLBAR_BUTTON_CLASS}
        >
          {t("analytics.services.detail.reviews.evaluations", "Evaluations")}
        </Button>
      </div>

      <MetricCardsGrid
        columns={4}
        className="mb-6 w-full !grid-cols-2 xl:!grid-cols-4"
      >
        <MetricCard
          title={t(
            "analytics.services.detail.reviews.overallRating",
            "Overall Rating",
          )}
          value={summary.overallRating.toFixed(1)}
          width="fill"
        />
        <MetricCard
          title={t(
            "analytics.services.detail.reviews.positiveNeutral",
            "Positive/Neutral",
          )}
          value={`${summary.positiveNeutralPercent}%`}
          width="fill"
        />
        <MetricCard
          title={t("analytics.services.detail.reviews.negative", "Negative")}
          value={`${summary.negativePercent}%`}
          width="fill"
        />
        <MetricCard
          title={t(
            "analytics.services.detail.reviews.reviewsCount",
            "Reviews Count",
          )}
          value={String(summary.reviewsCount)}
          width="fill"
        />
      </MetricCardsGrid>

      <DataTable
        data={paginatedReviews}
        columns={columns}
        getRowKey={(row) => row.id}
        columnHeaderClassName="text-[10px] font-medium text-[#737373]"
        emptyMessage={t(
          "analytics.services.detail.reviews.empty",
          "No reviews yet.",
        )}
        withSorting={false}
        withPagination={totalPages > 1}
        pagination={
          totalPages > 1
            ? {
                currentPage: safePage,
                totalPages,
                onPageChange: setCurrentPage,
              }
            : undefined
        }
      />
    </RevenueChartCard>
  );
};
